import i18n from 'helpers/i18n';
import messages from './messages';
import { clsx } from 'clsx';
import { useEffect } from 'react';
import useAsync from 'hooks/useAsync';
import { useMeasure } from 'context/measure';
import { Icon, Loader } from 'semantic-ui-react';
import { scoreToRange } from 'helpers/dimension';
import { abbrToUppercase } from 'helpers/dimension';
import ScoresByCycleTable from './ScoresByCycleTable';
import { ObservationAverages } from 'types/api/envScales/Observation';
import { getObservationAverages } from 'actions/envScales/observations';
import { getDomainColors } from './domainColors';

import {
  Section,
  SectionHeader,
  SectionContent
} from 'components/Measure/View/Styled';

import {
  Wrapper,
  GridWrapper,
  DashedLine,
  ScoreLine,
  ScoreWrapper,
  DomainWrapper,
  DomainName,
  DomainScore,
  DimensionWrapper,
  DimensionName,
  DimensionIcon,
  DimensionScore
} from './Styled';

/**
 * The endpoint sends 0 when it has no average; scores start at 1, so 0 is never
 * a real one.
 */
function hasAverage(average: number) {
  return average > 0;
}

function NoAverage() {
  return (
    <>
      <span aria-hidden>-</span>
      <span className="sr-only">{i18n.ft(messages.notScored)}</span>
    </>
  );
}

interface AverageProps {
  average: number;
  isInformal: boolean;
}

/** Domains have no range, so an informal observation shows nothing. */
function DomainAverage({ average, isInformal }: AverageProps) {
  if (isInformal) {
    return null;
  }

  return hasAverage(average) ? <>{average.toFixed(2)}</> : <NoAverage />;
}

function DimensionAverage({ average, isInformal }: AverageProps) {
  if (!hasAverage(average)) {
    return <NoAverage />;
  }

  return <>{isInformal ? scoreToRange(average) : average.toFixed(2)}</>;
}

interface ObservationScoresProps {
  fitted?: boolean;
}

function ObservationScores({ fitted }: ObservationScoresProps) {
  const { assessment, refresh, isRefreshing } = useMeasure();
  const isInformal = assessment.informal;
  const observationId = assessment.observation!.id;
  const { run, data, isPending } = useAsync();

  useEffect(() => {
    run(getObservationAverages(observationId));
  }, [run, observationId]);

  /**
   * Scores are entered on sibling routes sharing this provider, which loads
   * once. Revalidate so this is not showing the scores as first opened.
   */
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Wait for the refresh too, so the table mounts against fresh scores.
  if (isPending || isRefreshing) {
    return <Loader active inline="centered" />;
  }

  const numberOfScores = (data as ObservationAverages).average_scores.length;
  const sectionClasses = clsx(
    !fitted && 'mt-8 sm:mt-10',
    assessment.includes_environments ? 'mt-2 rounded-t-none' : 'rounded-t-xl'
  );

  return (
    <Section className={sectionClasses}>
      <SectionHeader>
        {isInformal ? i18n.ft(messages.titleInformal) : i18n.ft(messages.title)}
      </SectionHeader>
      <SectionContent>
        <Wrapper>
          <GridWrapper scores={numberOfScores}>
            <DashedLine />
            {(data as ObservationAverages).average_scores.map((domain, idx) => {
              const color = getDomainColors(domain.abbreviation);

              return (
                <ScoreWrapper key={domain.abbreviation}>
                  {idx !== 0 && <ScoreLine />}

                  <DomainWrapper className={clsx(color.background, color.text)}>
                    <DomainName>{domain.name}</DomainName>
                    <DomainScore>
                      <DomainAverage
                        average={domain.average}
                        isInformal={isInformal}
                      />
                    </DomainScore>
                  </DomainWrapper>
                  {domain.dimensions.map(dimension => (
                    <DimensionWrapper key={dimension.abbreviation}>
                      <DimensionName
                        title={dimension.name}
                        className={color.text}
                      >
                        {abbrToUppercase(dimension.abbreviation)}
                      </DimensionName>
                      <DimensionIcon>
                        <Icon size="big" name="long arrow alternate right" />
                      </DimensionIcon>
                      <DimensionScore
                        className={clsx(color.background, color.text)}
                      >
                        <DimensionAverage
                          average={dimension.average}
                          isInformal={isInformal}
                        />
                      </DimensionScore>
                    </DimensionWrapper>
                  ))}
                </ScoreWrapper>
              );
            })}
          </GridWrapper>
        </Wrapper>

        <div className="mt-7">
          <ScoresByCycleTable />
        </div>
      </SectionContent>
    </Section>
  );
}

export default ObservationScores;
